import { useState, useCallback, useEffect, useRef } from "react";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { BaseRecord, useList } from "@refinedev/core";

import { PaginatedTableOptions, PaginatedTableResult } from "./types";

export function usePaginatedTable<T extends BaseRecord>({
  resource,
  initialPage = 1,
  initialPageSize = 10,
  filters = {},
  sorters = {},
  enabled = true,
}: PaginatedTableOptions): PaginatedTableResult<T> {
  const parseInitialSortModel = (sorters: {
    ordering?: string;
  }): GridSortModel => {
    if (!sorters.ordering) return [];

    const field = sorters.ordering.startsWith("-")
      ? sorters.ordering.substring(1)
      : sorters.ordering;

    const sort = sorters.ordering.startsWith("-") ? "desc" : "asc";

    return [{ field, sort }];
  };

  const [sortModel, setSortModel] = useState<GridSortModel>(
    parseInitialSortModel(sorters)
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: initialPage - 1,
    pageSize: initialPageSize,
  });

  const filtersKey = JSON.stringify(filters);
  const previousFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (previousFiltersKey.current === filtersKey) return;
    previousFiltersKey.current = filtersKey;
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [filtersKey]);

  const { data, isLoading, refetch, isFetching, isError } = useList({
    resource: resource,
    // staleTime global é 5min e as mutations invalidam keys que não batem com a do refine
    queryOptions: { enabled, refetchOnMount: "always" },
    filters: [
      ...Object.entries(filters).map(([key, value]) => ({
        field: key,
        operator: "eq" as const,
        value,
      })),
      { field: "page", operator: "eq", value: paginationModel.page + 1 },
      { field: "page_size", operator: "eq", value: paginationModel.pageSize },
      {
        field: "ordering",
        operator: "eq" as const,
        value:
          sortModel.length > 0
            ? `${sortModel[0].sort === "desc" ? "-" : ""}${sortModel[0].field}`
            : sorters.ordering,
      },
    ],
  });

  const onPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  const onSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const formatedData: any = data?.data;

  return {
    rows: Array.isArray(formatedData)
      ? formatedData
      : formatedData?.results ?? [],
    rowCount: formatedData?.count ?? 0,
    loading: isLoading || isFetching,
    isError,
    paginationModel,
    sortModel,
    refetch,
    onPaginationModelChange,
    onSortModelChange,
  };
}
